import React, { useReducer, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import Fire from '@images/icons/double-fire.svg';

import Modal from '../ProfileV2/shared/Modal';
import StepOne from '../NotifyLoop/steps/StepOne';
import StepTwo from '../NotifyLoop/steps/StepTwo';
import StepThree from '../NotifyLoop/steps/StepThree';
import LeadTypePill from './LeadTypePill';
import PromptForTitleModal from '../NotifyLoop/PromptForTitleModal';
import PriorRelationshipConfirmationModal from '../NotifyLoop/PriorRelationshipConfirmationModal';
import { logCrmEvent } from '@javascripts/util/eventLogging';

import { cleanCurrency } from '../util/textMasks';

const token = () => document.querySelector('meta[name="csrf-token"]').content;

const PRIOR_RELATIONSHIP_IDS = [107];
const PROMPT_FOR_TITLE_OPTIONS = [
  83,
  116,
  84,
  85,
  131,
  132,
];

const initialState = () => ({
  noteable: {
    agent_closed_at: '',
    agent_commission: '',
    agent_listed_at: '',
    listing_price: '',
    mls_number: '',
    purchase_property_address: '',
  },
  check_details: {
    check_amount: '',
    check_date: '',
    check_number: '',
  },
  closing_info: {
    title_company: '',
    mortgage_lender: '',
    loan_officer: '',
    loan_amount: '',
    interest_rate: '',
  },
  reminder_attributes: {
    alert_datetime: '',
  },
  closing_disclosure: null,
  statusStep: '1',
  grouping: '',
  note_status_id: '',
  prior_relationship: null,
  currently_working: null,
  message: '',
  send_referral_form: 0,
  updating: false,
  messageFocused: false,
  reminderOption: '',
  reminderDate: '',
  reminderTime: '',
  reminderHour: '',
  reminderMinutes: '',
  reminderAmPm: 'AM',
  showConfirmLeaveModal: false,
  showConfirmPriorRelationshipModal: false,
  showPromptForTitleModal: false,
  forced: 1,
});

const closedEscrowNoteStatusIds = [86, 87, 123, 124];

const handleDateBlur = (e) => {
  if (e.target.value.length < 1) {
    e.target.type = 'date';
  }
};

function scrollFormIntoView() {
  const firstScrollAnchor = document.getElementsByClassName('scroll-into-view')[0];

  if (firstScrollAnchor) {
    firstScrollAnchor.scrollIntoView({
      block: 'start',
      inline: 'nearest',
      behavior: 'smooth',
    });
  }
}

const ProgressReport = ({ data }) => {
  const {
    attention,
    closingDisclosureTitle,
    matchCreatedAt,
    email,
    isExclusive,
    isNew,
    leadType,
    location,
    match_id,
    match_uuid,
    mortgageLenderOptions,
    name: leadName,
    options,
    phoneOrMessage,
    promptForTitleServices,
    hideContact,
    gciPrice,
    timeline,
    title,
    paths: {
      updatePath,
    },
  } = data;

  const [state, setState] = useReducer(
    (prevState, newState) => ({ ...prevState, ...newState }),
    initialState(),
  );

  const [showDetails, setShowDetails] = useState(false);

  const {
    message,
    note_status_id,
    prior_relationship,
    currently_working,
    reminderDate,
    reminderHour,
    reminderMinutes,
    reminderAmPm,
    showConfirmPriorRelationshipModal,
    showPromptForTitleModal,
  } = state;

  const buildDate = () => {
    const newState = {
      reminder_attributes: {
        alert_datetime: '',
      },
    };

    if (reminderDate && reminderHour && reminderMinutes && reminderAmPm) {
      const hour = reminderAmPm === 'PM'
        ? parseInt(reminderHour, 10) + 12
        : reminderHour;

      newState.reminder_attributes.alert_datetime = new Date(
        `${reminderDate} ${hour}:${String(reminderMinutes).padStart(2, '0')}`
          .replace(/-/g, '/'),
      ).getTime();
    }

    setState(newState);
  };

  useEffect(() => {
    buildDate();
  }, [
    reminderDate,
    reminderHour,
    reminderMinutes,
    reminderAmPm,
  ]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const newState = { [name]: value };

    if (name === 'note_status_id') {
      const noteStatusId = parseInt(value, 10);
      newState.send_referral_form = +closedEscrowNoteStatusIds.includes(parseInt(noteStatusId, 10));

      setTimeout(scrollFormIntoView, 200);
    }

    setState(newState);
  };

  const handleMessageFocus = () => {
    setState({ messageFocused: true });
  };

  const handleMessageBlur = () => {
    if (message.length < 1) {
      setState({ messageFocused: false });
    }
  };

  const isUpdateDisabled = () => {
    const {
      reminderOption,
      prior_relationship,
      currently_working,
      reminder_attributes: {
        alert_datetime,
      },
    } = state;

    // Check if prior relationship details empty
    if (PRIOR_RELATIONSHIP_IDS.includes(parseInt(note_status_id, 10))) return (prior_relationship === null || currently_working === null);

    if (note_status_id) {
      if (reminderOption && !alert_datetime) {
        return true;
      }

      return false;
    }

    return true;
  };

  const close = () => {
    document.getElementById('progress-report-modal').classList.add('hidden');
  };

  const dismiss = () => {
    close();
    logCrmEvent('disruptor_dismissed', { type: 'progress_report', match_id });
  };

  const submit = async () => {
    const formData = new FormData();

    Object.keys(state).forEach((key) => {
      const valOrObject = state[key];

      if (valOrObject) {
        if (typeof valOrObject === 'object' && !(valOrObject instanceof File)) {
          Object.keys(valOrObject).forEach((nestKey) => {
            const val = ['listing_price', 'check_amount', 'loan_amount'].includes(nestKey)
              ? cleanCurrency(valOrObject[nestKey])
              : valOrObject[nestKey];

            formData.append(`${key}[${nestKey}]`, val);
          });
        } else {
          formData.append(key, valOrObject);
        }
      }
    });

    const request = await fetch(updatePath, {
      method: 'POST',
      headers: {
        'X-CSRF-Token': token(),
      },
      credentials: 'include',
      body: formData,
    });

    const response = await request.json();

    const { errors } = response;

    if (errors) {
      console.warn('Update Status Error', errors);
    } else if (PRIOR_RELATIONSHIP_IDS.includes(parseInt(note_status_id, 10))) {
      setState({ showConfirmPriorRelationshipModal: true });
    } else if (promptForTitleServices && PROMPT_FOR_TITLE_OPTIONS.includes(parseInt(note_status_id, 10))) {
      setState({ showPromptForTitleModal: true });

      logCrmEvent(
        'disruptor_viewed',
        {
          type: 'ress_contact_request_from_agent_status_update',
          note_status_id,
          match_id,
        },
      );
    } else {
      logCrmEvent('disruptor_submitted', { type: 'progress_report', match_id });
      close();
    }
  };

  const toggleDetails = () => {
    setShowDetails(!showDetails);
  };

  const dismissCloseTitleModal = () => {
    setState({ showPromptForTitleModal: false });
    close();
  };

  const acceptCloseTitleModal = () => {
    dismissCloseTitleModal();

    if (window.updateNotice) {
      window.updateNotice(
        {
          header: 'Success',
          message: 'Update received! Our Title Rep will be in touch.',
        },
        5000,
      );
    }
  };

  const firstTwoTimeline = timeline.slice(0, Math.min(2, timeline.length));
  const detailsClassList = ['lead-details'];
  const buttonClassList = ['large-bold-font', 'show-more'];

  if (showDetails) {
    detailsClassList.push('full');
    buttonClassList.push('full');
  }

  const modalContent = () => (
    <div className="update-status-container">
      <div className="update-status-content">
        <div className={detailsClassList.join(' ')}>
          <div className="lead-details-top">
            <div className="lead-name">
              <h2 className="headline-2">
                {leadName}
              </h2>
            </div>
            <div className="lead-type">
              <div className="contact-lead-type pill-container">
                <LeadTypePill
                  gciPrice={gciPrice}
                  leadType={leadType}
                  exclusive={isExclusive}
                />
              </div>
            </div>
            <div className="lead-location">
              <div className="large-slim-font">
                {location}
              </div>
              <div className="details-container">
                <button
                  type="button"
                  className={buttonClassList.join(' ')}
                  onClick={toggleDetails}
                >
                  {
                    showDetails
                      ? 'Hide Details'
                      : 'Show Details'
                  }
                </button>
              </div>
            </div>
          </div>
          <div className="lead-details-bottom">
            <div className="lead-contact large-slim-font">
              {
                hideContact
                  ? (
                    <div className="lead-contact-none large-slim-font">
                      <p>
                        Client has removed their contact information
                        <br />
                        from our system
                      </p>

                    </div>
                  )
                  : (
                    <>
                      <div className="lead-contact-phone">
                        <a
                          className="large-slim-font"
                          href={phoneOrMessage.value}
                          id="client-info-phone"
                          method="call"
                          name="client-info-phone"
                          rel="noopener noreferrer"
                          target="_blank"
                          trigger="click_phone_number"
                          type="tel"
                        >
                          <i className="phone" />
                          {phoneOrMessage.text}
                        </a>
                      </div>
                      <div className="lead-contact-email large-slim-font">
                        <a
                          className="large-slim-font"
                          href={email.mailTo}
                          id="client-info-email"
                          method="email"
                          name="client-info-email"
                          rel="noopener noreferrer"
                          target="_blank"
                          trigger="click_email_address"
                          type="email"
                        >
                          <i className="email" />
                          {email.text}
                        </a>
                      </div>
                    </>
                  )
              }
            </div>
            <div className="lead-timeline">
              {
                firstTwoTimeline.map((tl, idx) => (
                  <div key={idx} className="timeline-event small-slim-font">
                    {`${tl.headline} on ${tl.createdAt}`}
                  </div>
                ))
              }
            </div>
          </div>
        </div>
        <StepOne
          state={state}
          categoryOptions={options}
          matchCreatedAt={matchCreatedAt}
          closingDisclosureTitle={closingDisclosureTitle}
          mortgageLenderOptions={mortgageLenderOptions}
          handleChange={handleChange}
          handleDateBlur={handleDateBlur}
          updateState={setState}
          collapsible
        />
        <StepTwo
          state={state}
          handleChange={handleChange}
          handleMessageFocus={handleMessageFocus}
          handleMessageBlur={handleMessageBlur}
          updateState={setState}
          collapsible
        />
        <StepThree
          state={state}
          handleDateBlur={handleDateBlur}
          buildDate={buildDate}
          handleMessageBlur={handleMessageBlur}
          updateState={setState}
          collapsible
        />
      </div>
    </div>
  );

  const footer = () => (
    <div className="footer">
      <button
        type="submit"
        className="profile-edit-button submit large-bold-font"
        onClick={submit}
        disabled={isUpdateDisabled()}
      >
        {
          isNew
            ? 'Update and view new referral'
            : 'Update'
        }
      </button>
    </div>
  );

  const hidePriorRelationshipModal = (e) => {
    e.stopPropagation();
    setState({ showConfirmPriorRelationshipModal: false });
    close();
  }

  return (
    <Modal
      id="progress-report-modal"
      status="active"
      close={dismiss}
      showFooter
      footerContent={footer()}
      contentClass="edit-modal"
    >
      { showConfirmPriorRelationshipModal &&
        <PriorRelationshipConfirmationModal
          leadName={leadName}
          matchCreatedAt={matchCreatedAt}
          show={showConfirmPriorRelationshipModal}
          hideModal={hidePriorRelationshipModal}
        />
      }
      <div className="edit-modal-inner">
        <button
          type="button"
          className="close-button"
          onClick={dismiss}
        >
          <i className="times" />
        </button>
        {
          isNew && (
            <div className="attention large-bold-font">
              <img src={Fire} alt="" />
              {attention}
            </div>
          )
        }
        <h2 className="headline-2 title">
          {
            isNew
              ? `First, d${title.substring(1)}`
              : title
          }
        </h2>
        {modalContent()}
      </div>
      <PromptForTitleModal
        show={showPromptForTitleModal}
        acceptClose={acceptCloseTitleModal}
        dismissClose={dismissCloseTitleModal}
        matchId={match_id}
        matchUuid={match_uuid}
        noteStatusId={note_status_id}
      />
    </Modal>
  );
};

export default ProgressReport;

export const TIMELINE_EVENT = PropTypes.shape({
  created_at: PropTypes.string,
  body: PropTypes.string,
  headline: PropTypes.string,
  icon: PropTypes.string,
  message_path: PropTypes.string,
});

ProgressReport.propTypes = {
  data: PropTypes.shape({
    attention: PropTypes.string.isRequired,
    closingDisclosureTitle: PropTypes.string.isRequired,
    email: PropTypes.shape({
      value: PropTypes.string.isRequired,
      verified: PropTypes.bool.isRequired,
      text: PropTypes.string.isRequired,
      mailTo: PropTypes.string.isRequired,
    }).isRequired,
    hideContact: PropTypes.bool.isRequired,
    isExclusive: PropTypes.bool.isRequired,
    isNew: PropTypes.bool,
    leadType: PropTypes.string.isRequired,
    location: PropTypes.string.isRequired,
    match_id: PropTypes.number.isRequired,
    match_uuid: PropTypes.string,
    mortgageLenderOptions: PropTypes.arrayOf(PropTypes.string).isRequired,
    name: PropTypes.string.isRequired,
    options: PropTypes.arrayOf(PropTypes.shape({
      id: PropTypes.number,
      display: PropTypes.string,
    })).isRequired,
    phoneOrMessage: PropTypes.shape({
      text: PropTypes.string.isRequired,
      value: PropTypes.string.isRequired,
    }).isRequired,
    gciPrice: PropTypes.string.isRequired,
    promptForTitleServices: PropTypes.bool.isRequired,
    timeline: PropTypes.arrayOf(TIMELINE_EVENT).isRequired,
    title: PropTypes.string.isRequired,
    paths: PropTypes.shape({
      updatePath: PropTypes.string,
    }).isRequired,
  }).isRequired,
};
